/**
 * Wire contract between the Kola server and the clients it serves.
 *
 * Until 2026-09 the client and the server always shipped together (one
 * container per master push), so "which client talks to which server" was
 * never a question. The desktop app now bundles the Kola client and ships
 * it on its own, owner-gated release cadence, while the server keeps
 * auto-deploying — so at any moment several client builds talk to one
 * server. The rules that keep that safe:
 *
 *  - REST/WS changes are ADDITIVE by default (new routes, optional fields,
 *    new Yjs keys): no bump needed, every older client keeps working.
 *  - A change an older client would misread (a route or field changing
 *    meaning, a WS protocol change, a guarded-writer rule both sides must
 *    agree on) bumps `KOLA_API_CONTRACT`, ships the new client in a REQUIRED
 *    desktop update first, and only THEN raises `KOLA_MIN_CLIENT_CONTRACT`
 *    on the server — stragglers get 426 and a "update the app" screen instead
 *    of a subtly broken board.
 *
 * The server advertises both numbers on `GET /api/auth/mode` and on every
 * `/api` response (`x-kola-contract`); an embedded client sends
 * `x-kola-client: <version>/<contract>` so the server can log skew and
 * refuse a client below the floor.
 */
/**
 * The contract this build of the code speaks. Bump per the rules above.
 *
 * History:
 *  - 2: `POST /api/boards` requires `scriptFinishDate` + `assignees` (F-135).
 *    A contract-1 client's create call is refused with 400 — the board list,
 *    boards themselves and every other route are unchanged, so the floor
 *    stays at 1 until the desktop build that sends the fields is required.
 */
export const KOLA_API_CONTRACT = 2;
/** The oldest client contract the server still serves; never above the current. */
export const KOLA_MIN_CLIENT_CONTRACT = 1;
/** Response header carrying the server's contract on every /api reply. */
export const KOLA_CONTRACT_HEADER = "x-kola-contract";
/** Request header an embedded client sends: `<clientVersion>/<contract>`. */
export const KOLA_CLIENT_HEADER = "x-kola-client";
export function formatKolaClientHeader(version, contract) {
    return `${version}/${contract}`;
}
/**
 * Parse `x-kola-client`. Null for a missing or malformed value — a client
 * that sends nothing (the web SPA, curl) is treated as current, because the
 * header exists to protect EMBEDDED clients from skew, not to gate the SPA
 * the server itself serves.
 */
export function parseKolaClientHeader(value) {
    if (typeof value !== "string")
        return null;
    const match = /^([A-Za-z0-9.+-]{1,64})\/(\d{1,6})$/.exec(value.trim());
    if (!match)
        return null;
    return { version: match[1], contract: Number(match[2]) };
}
//# sourceMappingURL=contract.js.map